import { z } from 'zod'
import type { LowerHex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import { addressPrimitiveSchema, bigintTextSchema } from '../../types/primitive'
import type { EtherscanV2Client } from './client'

type GetInternalTransactionsByTransactionHashParameters = {
  client: EtherscanV2Client
  txhash: LowerHex
  headers?: Record<string, string>
}

const internalTransactionSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  contractAddress: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  from: addressPrimitiveSchema,
  gas: bigintTextSchema,
  isError: z
    .string()
    .regex(/^\d+$/)
    .transform((x) => x === '1'),
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  to: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  value: bigintTextSchema,
})
const resultSchema = z.array(internalTransactionSchema)

/** {@link https://docs.etherscan.io/etherscan-v2/api-endpoints/accounts#get-internal-transactions-by-transaction-hash} */
export const getInternalTransactionsByTransactionHash = async ({
  client,
  txhash,
  headers,
}: GetInternalTransactionsByTransactionHashParameters): Promise<
  InternalTransactionWithIndex[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlistinternal',
        txhash,
      },
      resultSchema,
      headers,
    })
    return result.map((v) => ({
      blockNumber: v.blockNumber,
      contractAddress: v.contractAddress,
      from: v.from,
      gas: v.gas,
      isError: v.isError,
      timestamp: v.timeStamp * 1000,
      to: v.to,
      txHash: txhash,
      value: v.value,
    }))
  } catch (error) {
    if (error instanceof Error && error.message === 'No transactions found') {
      return []
    }
    throw error
  }
}
